import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { getShortcutReferenceData, formatShortcutKeys } from '@/lib/keyboard-shortcuts';
import { Keyboard } from 'lucide-react';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const KeyboardShortcutsModal = ({ open, onOpenChange }: KeyboardShortcutsModalProps) => {
  const referenceData = getShortcutReferenceData();

  const categories = [
    { id: 'navigation', label: 'Navigation', icon: '🧭' },
    { id: 'search', label: 'Search', icon: '🔍' },
    { id: 'actions', label: 'Actions', icon: '⚡' },
    { id: 'general', label: 'General', icon: '⚙️' },
  ] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard size={24} />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Speed up your workflow with these keyboard shortcuts. Press ? anytime to open this menu.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="navigation" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            {categories.map((category) => (
              <TabsTrigger key={category.id} value={category.id} className="flex items-center gap-1">
                <span>{category.icon}</span>
                <span className="hidden sm:inline">{category.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {categories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="space-y-3">
              {referenceData[category.id as keyof typeof referenceData].length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No shortcuts in this category</p>
              ) : (
                <div className="space-y-2">
                  {referenceData[category.id as keyof typeof referenceData].map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition"
                    >
                      <p className="text-sm font-medium text-slate-700">{item.description}</p>
                      <Badge variant="outline" className="font-mono text-xs">
                        {item.keys}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="pt-4 border-t">
          <p className="text-xs text-slate-500">
            💡 Tip: Most navigation shortcuts follow the pattern <kbd className="px-2 py-1 bg-slate-100 rounded text-xs">G</kbd> + <kbd className="px-2 py-1 bg-slate-100 rounded text-xs">First Letter</kbd>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
